import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NotInterested } from '@mui/icons-material';
import { Box, Card, Divider, Tooltip } from '@mui/material';
import { observer } from 'mobx-react-lite';
import {
  CompanySelect,
  CompanyTotals,
  FuseTabs,
  Headlines,
  LenderSelect,
  LoanSelect,
  MonthlyActivity,
  TransactionsLedger,
} from '/src/components';
import { useDateStore, useFilterStore, useUserStore } from '/src/context';
import { DesktopLayout } from '/src/layouts';
import { Loan } from '/src/lib/models';

export const DashboardPage: React.FC = observer(() => {
  /** Context **/
  const userStore = useUserStore();
  const filterStore = useFilterStore();
  const navigate = useNavigate();
  const dateStore = useDateStore();

  /** State **/
  const [companyFilter, setCompanyFilter] = useState(filterStore.companyGet());
  const [loanFilter, setLoanFilter] = useState(filterStore.loanGet());
  const [lenderFilter, setLenderFilter] = useState(filterStore.lenderGet());

  /** Computed **/
  const companies = useMemo(
    () => filterStore.companyListGet(['name', '_id', 'tags']).filter((company) => !company.isLender),
    [filterStore._companyList]
  );

  const loans = useMemo(
    () =>
      filterStore.loanListGet(['name', '_id', 'tags', 'borrower', 'lenders']).filter((l) => {
        if (companyFilter?._id || lenderFilter?._id) {
          if (companyFilter?._id) return l.borrower._id === companyFilter._id;
          if (lenderFilter?._id)
            return !!l.lenders?.find((participation) => participation.company === lenderFilter._id);
        } else return true;
      }),
    [filterStore._loanList, companyFilter?._id, lenderFilter?._id]
  );

  const lenders = useMemo(() => {
    const l = filterStore.lenderListGet(null, ['name', '_id', 'tags']);
    return Array.isArray(l)
      ? l.filter((lender) => {
          return loanFilter?._id
            ? loanFilter.lenders?.find((participation) => participation.company === lender._id)
            : true;
        })
      : l && l._id
      ? [l]
      : [];
  }, [filterStore._lenderList, loanFilter]);

  /** Methods **/

  /**
   * Resets the filter dropdowns.
   */
  const resetFilters = () => {
    setCompanyFilter(undefined);
    setLoanFilter(undefined);
    setLenderFilter(undefined);

    // Reset date range
    dateStore.setCurrentDate(new Date());
    dateStore.calculateRange();
  };

  /** Effects **/

  useEffect(() => {
    if (!userStore.user) navigate('/signin');

    // If user belongs to a company, add that company as a filter
    if (userStore && userStore.user && !userStore.isLender) {
      setCompanyFilter({
        _id: userStore.user.company,
      });
    }
  }, []);

  useEffect(() => {
    if (lenders) filterStore.companyListUpdateLenders();
  }, [filterStore._companyList, lenders]);

  useEffect(() => {
    if (companies) filterStore.companyListUpdateLenders();
  }, [filterStore._lenderList, companies]);

  /** Render **/
  return (
    <DesktopLayout>
      <Box display="flex" flexDirection="column">
        <Card component="md-card" className="margin-top-10">
          <Box display="flex" justifyContent="top" alignItems="center" bgcolor="white" className="pad-sides">
            {/* COMPANY SELECT */}
            <Box display="flex" flex={1} justifyContent="center" alignItems="center" className="pad-right">
              <CompanySelect
                value={companyFilter?._id ?? ''}
                setValue={(companyId) => {
                  setLenderFilter(undefined);
                  setLoanFilter(undefined);
                  setCompanyFilter(companies.find((company) => company._id === companyId));
                }}
                companies={companies}
                ariaLabel="Select a Company"
                placeholder="All Companies"
                enablePlaceholderOption
                boxProps={{ width: '100%' }}
                className="md-primary no-margin-top-bottom"
              />
            </Box>

            {/* LOAN SELECT */}
            <Box display="flex" flex={1} justifyContent="center" alignItems="center" className="pad-right">
              <LoanSelect
                value={loanFilter?._id ?? ''}
                setValue={(loanId) => setLoanFilter(loans.find((loan) => loan._id === loanId))}
                loans={loans}
                ariaLabel="Select a Deal"
                placeholder="All Deals"
                enablePlaceholderOption
                boxProps={{ width: '100%' }}
                className="md-primary no-margin-top-bottom"
              />
            </Box>

            {/* LENDER SELECT */}
            <Box display="flex" flex={1} justifyContent="center" alignItems="center" className="pad-right">
              <LenderSelect
                value={lenderFilter?._id ?? ''}
                setValue={(lenderId) => setLenderFilter(lenders.find((lender) => lender._id === lenderId))}
                lenders={lenders}
                ariaLabel="Select an Investor"
                placeholder="All Investors"
                enablePlaceholderOption
                boxProps={{ width: '100%' }}
                className="md-primary no-margin-top-bottom"
              />
            </Box>

            {(companyFilter?._id || loanFilter?._id || lenderFilter?._id) && (
              <Box component="span" className="right" layout-align="center center">
                <Box sx={{ cursor: 'pointer' }} onClick={() => resetFilters()}>
                  <Tooltip title="Clear Filters">
                    <NotInterested className="pad-left-xs" />
                  </Tooltip>
                </Box>
              </Box>
            )}
          </Box>

          <Divider />

          <Box display="flex" justifyContent="top" alignItems="center" bgcolor="white">
            <Headlines company={companyFilter} loan={loanFilter} lender={lenderFilter} />
          </Box>

          <Divider />

          <FuseTabs defaultTab={0}>
            {[
              {
                index: 0,
                label: 'Monthly Activity',
                content: (
                  <MonthlyActivity companyFilter={companyFilter} loanFilter={loanFilter} lenderFilter={lenderFilter} />
                ),
              },
              {
                index: 1,
                label: 'Transactions',
                content: <TransactionsLedger loan={loanFilter as Loan} />,
              },
            ]}
          </FuseTabs>
        </Card>

        {(userStore.isAdmin || userStore.isLender) && <CompanyTotals />}
      </Box>
    </DesktopLayout>
  );
});
